import { IComboBoxOption } from '@fluentui/react';
import LazyInitialization from '../../lib/LazyInitialization';
import XhrRequestHandler from '../../lib/XhrRequestHandler';
import { Collaborator } from '../collaborators/Collaborator';
import { User } from '../User';
import { OrganizationChartIntermediateNode } from './OrganizationChartIntermediateNode';
import { OrganizationChartLevel } from './OrganizationChartLevel';
import { OrganizationChartNode } from './OrganizationChartNode';
import { OrganizationChartTopNode } from './OrganizationChartTopNode';
import { NodoOrganigramaDataGrid } from './nodo-organigrama-datagrid.interface';

interface IOrganizationChartLevel {
  nio_id: string;
  nio_nombre: string;
  nio_nombre_tec: string;
}

interface IIComboBoxOptionLeves {
  levels:IComboBoxOption[]
}

interface IOrganizationChartNode {
  nor_id: string;
  nor_nombre: string;
  nor_nivel: string;
  nor_padre: string | null;
}

export interface IOrganizationChartCompleteNode extends IOrganizationChartNode {
  nodoOrganigrama: IOrganizationChartCompleteNode | null;
}

export interface SaveCostCentersResponse {
  success: boolean;
  updated: number;
  errors: string[];
}


class OrganizationChartSystem {
  private readonly entityBase = '/organigrama';

  private levels: LazyInitialization<OrganizationChartLevel[]>;
  private nodes: LazyInitialization<OrganizationChartNode[]>;

  constructor(private requestHandler: XhrRequestHandler) {
    this.levels = new LazyInitialization(() => this.getLevelsRequest());
    this.nodes = new LazyInitialization(() => this.getAllNodesRequest());
  }

  getBaseUrlForDatagrid() {
    return this.requestHandler.requestUrl(`${this.entityBase}/datagrid`);
  }

  getDataGrid(limit: number, filters?: { name: string; value: string; operator: string }[]) {
    const params = new URLSearchParams();
    params.append('skip', '0');
    params.append('limit', limit.toString());

    if (filters && filters.length > 0) {
      filters.forEach((filter, index) => {
        params.append(`filter[${index}][name]`, filter.name);
        params.append(`filter[${index}][value]`, filter.value);
        params.append(`filter[${index}][operator]`, filter.operator);
      });
    }

    const url = `${this.entityBase}/datagrid?${params.toString()}`;
    return this.requestHandler.get<NodoOrganigramaDataGrid>(url);
  }


  async saveCostCenters(data: { nor_id: string; nor_cecos: string | null }[]) {
    return this.requestHandler.post<SaveCostCentersResponse>(`${this.entityBase}/save-cost-centers`, data );
  }


  getLevels(reload = false): Promise<OrganizationChartLevel[]> {
    return this.levels.value(reload);
  }

  async getLastLevel(): Promise<OrganizationChartLevel> {
    const l = await this.getLevels();
    return [...l].pop()!;
  }

  async getAllNodes(reload = false): Promise<OrganizationChartNode[]> {
    return this.nodes.value(reload);
  }

  async getLastLevelNodes(): Promise<OrganizationChartNode[]> {
    const [nodes, levels] = await Promise.all([
      this.requestHandler.get<IOrganizationChartCompleteNode[]>(`${this.entityBase}/last-level-nodes`),
      this.getLevels(),
    ]);

    return nodes.map((node) => this.mapNode(node, levels));
  }

  async getLastLevelNodesss(level:string): Promise<IIComboBoxOptionLeves> {
    const results = await  this.requestHandler.get<IIComboBoxOptionLeves>(`${this.entityBase}/level-all-nodes/${level}`);
    return results;
  }

  async getNode(nodeId: string): Promise<OrganizationChartNode> {
    return this.nodes.withCachedValueDo(
        (data) => {
          const node = data.find((node) => node.isIdentifiedBy(nodeId));
          if (!node) {
            throw new Error('Node not found');
          }
          return Promise.resolve(node);
        },
        () => this.getNodeByIdRequest(nodeId)
    );
  }

  private async getNodeByIdRequest(nodeId: string): Promise<OrganizationChartNode> {
    const [node, levels] = await Promise.all([
      this.requestHandler.get<IOrganizationChartCompleteNode>(`${this.entityBase}/node/${nodeId}`),
      this.getLevels(),
    ]);

    return this.mapNode(node, levels);
  }

  mapNode(node: IOrganizationChartCompleteNode, levels: OrganizationChartLevel[]): OrganizationChartNode {
    const level = this.levelById(levels, Number(node.nor_nivel));
    if (node.nodoOrganigrama) {
      const parent = this.mapNode(node.nodoOrganigrama, levels);
      return OrganizationChartIntermediateNode.named(node.nor_nombre, node.nor_id, level, parent);
    }
    return OrganizationChartTopNode.named(node.nor_nombre, node.nor_id, level);
  }

  private levelById(levels: OrganizationChartLevel[], l: number): OrganizationChartLevel {
    return levels.find((level) => level.isIdentidiedBy(l))!;
  }

  private async getLevelsRequest(): Promise<OrganizationChartLevel[]> {
    const data = await this.requestHandler.get<IOrganizationChartLevel[]>(`${this.entityBase}/levels`);

    return data
        .sort((a, b) => Number(a.nio_id) - Number(b.nio_id))
        .map((level) => OrganizationChartLevel.named(level.nio_nombre, Number(level.nio_id), level.nio_nombre_tec));
  }

  private async getAllNodesRequest(): Promise<OrganizationChartNode[]> {
    const [nodes, levels] = await Promise.all([
      this.requestHandler.get<IOrganizationChartNode[]>(`${this.entityBase}/nodes`),
      this.getLevels(),
    ]);

    const result: OrganizationChartNode[] = [];
    nodes
        .sort((a, b) => Number(a.nor_nivel) - Number(b.nor_nivel))
        .forEach((node) => {
          const level = this.levelById(levels, Number(node.nor_nivel));
          if (node.nor_padre) {
            const parent = result.find((possibleParent) =>
                possibleParent.isIdentifiedBy(node.nor_padre || '')
            )!;
            result.push(OrganizationChartIntermediateNode.named(node.nor_nombre, node.nor_id, level, parent));
          } else {
            result.push(OrganizationChartTopNode.named(node.nor_nombre, node.nor_id, level));
          }
        });

    return result;
  }

  public async getNodeOf(col: Collaborator) {
    let pos = await col.getPosition();
    let node = await pos.getOrganizationNode();
    return node;
  }

  async getAvailableNodesOf(user: User) {
    const filtersDataNodes = await this.getAllNodes();
    const nodeOfLoggedCol = await this.getNodeOf(user.getCollaborator());
    let _nodesFiltered = filtersDataNodes;
    if (!user.hasPowerUserPrivileges()) {
      _nodesFiltered = filtersDataNodes.filter((node) => node.isDescendantOf(nodeOfLoggedCol));
    }
    return _nodesFiltered;
  }
}

export default OrganizationChartSystem;
